import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';

const CartContext = createContext(null);
const STORAGE_KEY = 'minishop_cart';

export const CartProvider = ({ children }) => {
  const [items, setItems] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem(STORAGE_KEY)) || [];
    } catch {
      return [];
    }
  });
  const [open, setOpen] = useState(false);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
  }, [items]);

  /**
   * Add a product to the cart.
   *
   * The line remembers **which shop** it belongs to (`shop_id` + the shop name /
   * username / currency when known) so a cart can hold products from several
   * shops while every shop is still paid on its own page — see `itemsOfShop`
   * and `removeShop` below.
   */
  const addItem = (product, quantity = 1, variations = {}, extra = {}, opts = {}) => {
    setItems((prev) => {
      const existing = prev.find(
        (i) => i.product_id === product.id && JSON.stringify(i.variations) === JSON.stringify(variations)
      );
      if (existing) {
        return prev.map((i) =>
          i.product_id === product.id && JSON.stringify(i.variations) === JSON.stringify(variations)
            ? { ...i, quantity: i.quantity + quantity }
            : i
        );
      }
      const image = product.images && product.images.length > 0 ? product.images[0] : '';
      return [...prev, {
        product_id: product.id,
        name: product.name,
        price: product.sale_price ?? product.price,
        image,
        quantity,
        variations,
        shop_id: product.shop_id,
        // Filled in when the product comes from the community shop page (it
        // carries its shop card) or when the shop page passes them along.
        shop_username: extra.shop_username || product.shop_username || product.shop?.username || '',
        shop_name: extra.shop_name || product.shop_name || product.shop?.shop_name || '',
        currency: extra.currency || product.currency || product.shop?.currency || '',
      }];
    });
    if (opts.open !== false) setOpen(true);      // the “buy account” modal may follow
    toast.success('Added to cart');
  };

  const removeItem = (index) => {
    setItems((prev) => prev.filter((_, i) => i !== index));
  };

  const updateQty = (index, qty) => {
    if (qty < 1) return;
    setItems((prev) => prev.map((i, idx) => (idx === index ? { ...i, quantity: qty } : i)));
  };

  const clear = () => setItems([]);

  /**
   * The lines of **one shop**. The cart can hold products of several shops (the
   * community shop page adds to any shop), but every shop is paid separately —
   * `shop_id` travels with each line, so checkout and the cart sidebar only look
   * at their own shop.
   */
  const itemsOfShop = (shopId) => items.filter((i) => i.shop_id === shopId);

  /** Drop every line of one shop (called after that shop's checkout). */
  const removeShop = (shopId) => setItems((prev) => prev.filter((i) => i.shop_id !== shopId));

  const totals = useMemo(() => {
    const subtotal = items.reduce((sum, i) => sum + i.price * i.quantity, 0);
    const count = items.reduce((sum, i) => sum + i.quantity, 0);
    return { subtotal: Math.round(subtotal * 100) / 100, count };
  }, [items]);

  return (
    <CartContext.Provider value={{ items, open, setOpen, addItem, removeItem, updateQty,
                                   clear, itemsOfShop, removeShop, totals }}>
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => useContext(CartContext);
